import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface DeliveryAutoReplySettings {
  autoReplyEnabled: boolean;
  greetingMessage: string | null;
  deliveryWhatsappNumber: string | null;
}

const DEFAULT_GREETING = `Olá! Bem-vindo ao {restaurante}! 🍽️

Acesse nosso cardápio digital e faça seu pedido:
👉 {link}

⏱️ Tempo de entrega: {tempo_estimado}
🚚 Taxa de entrega: R$ {taxa_entrega}
💰 Pedido mínimo: R$ {pedido_minimo}

Aguardamos seu pedido! 😊`;

export function useDeliverySettings() {
  const { activeRestaurant, refetch } = useRestaurantContext();
  const [isUpdating, setIsUpdating] = useState(false);
  const [settings, setSettings] = useState<DeliveryAutoReplySettings>({
    autoReplyEnabled: false,
    greetingMessage: null,
    deliveryWhatsappNumber: null,
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!activeRestaurant) return;
      const base: Partial<DeliveryAutoReplySettings> = {
        autoReplyEnabled: (activeRestaurant as any).delivery_auto_reply_enabled ?? false,
        greetingMessage: (activeRestaurant as any).delivery_greeting_message ?? null,
        deliveryWhatsappNumber: (activeRestaurant as any).delivery_whatsapp_number ?? null,
      };
      if (cancelled) return;
      setSettings({
        autoReplyEnabled: base.autoReplyEnabled ?? false,
        greetingMessage: base.greetingMessage ?? null,
        deliveryWhatsappNumber: base.deliveryWhatsappNumber ?? null,
      });
    }
    load();
    return () => { cancelled = true; };
  }, [activeRestaurant]);

  const updateSettings = useCallback(async (newSettings: Partial<DeliveryAutoReplySettings>) => {
    if (!activeRestaurant?.id) return false;

    setIsUpdating(true);
    try {
      const restaurantUpdate: Record<string, unknown> = {};
      if (newSettings.autoReplyEnabled !== undefined) {
        restaurantUpdate.delivery_auto_reply_enabled = newSettings.autoReplyEnabled;
      }
      if (newSettings.greetingMessage !== undefined) {
        restaurantUpdate.delivery_greeting_message = newSettings.greetingMessage;
      }
      if (newSettings.deliveryWhatsappNumber !== undefined) {
        restaurantUpdate.delivery_whatsapp_number = newSettings.deliveryWhatsappNumber;
      }
      if (Object.keys(restaurantUpdate).length > 0) {
        const { error } = await supabase
          .from("restaurants")
          .update(restaurantUpdate)
          .eq("id", activeRestaurant.id);
        if (error) throw error;
      }

      setSettings(prev => ({ ...prev, ...newSettings }));
      await refetch();
      toast.success("Configurações de auto-resposta salvas!");
      return true;
    } catch (error) {
      console.error("Error updating delivery settings:", error);
      toast.error("Erro ao salvar configurações");
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [activeRestaurant?.id, refetch]);

  const getWebhookUrl = useCallback(() => {
    return "https://atklcwnwxqopbxdmjvxc.supabase.co/functions/v1/whatsapp-webhook";
  }, []);

  const getMenuUrl = useCallback(() => {
    if (!activeRestaurant?.slug) return "";
    return `https://sejaaltus.com.br/menu/${activeRestaurant.slug}`;
  }, [activeRestaurant?.slug]);

  const getDefaultGreeting = useCallback(() => {
    return DEFAULT_GREETING;
  }, []);

  const testAutoReply = useCallback(async () => {
    if (!activeRestaurant?.id) return false;

    try {
      // Just validate settings are correct
      if (!settings.deliveryWhatsappNumber) {
        toast.error("Configure o número de WhatsApp primeiro");
        return false;
      }

      if (!settings.autoReplyEnabled) {
        toast.error("Ative a resposta automática primeiro");
        return false;
      }

      toast.success("Configuração válida! Envie uma mensagem para o número configurado para testar.");
      return true;
    } catch (error) {
      console.error("Error testing auto-reply:", error);
      toast.error("Erro ao testar configuração");
      return false;
    }
  }, [activeRestaurant?.id, settings]);

  return {
    settings,
    isUpdating,
    updateSettings,
    getWebhookUrl,
    getMenuUrl,
    getDefaultGreeting,
    testAutoReply,
  };
}
